import { Platform } from "react-native";
import { File, Paths } from "expo-file-system";
import { logAndFlush, flushQueue } from "./batch-logger";
import { recordCrashTombstone, type ICrashTombstone } from "./crash-reporter";
import { AppVersion, ProductVersion } from "@/versions";

const getCrashTombstoneFile = (): File | null => {
  try {
    return new File(Paths.cache, "last_crash.json");
  } catch {
    return null;
  }
};
const WEB_CRASH_STORAGE_KEY = "@memories/last_crash_tombstone";
const FIREBASE_PROJECT_ID = process.env.EXPO_PUBLIC_PROJECT_ID || "memories-dev-247";

export interface ICrashlyticsInfo {
  provider: "native_crashlytics" | "cloud_logging_hybrid";
  platform: string;
  collectionEnabled: boolean;
  projectId: string;
  googleServicesConfigured: boolean;
  appVersion: string;
  productVersion: string;
  consoleUrl: string;
}

let isCollectionEnabled = true;
let customAttributes: Record<string, string> = {
  appVersion: AppVersion,
  productVersion: ProductVersion,
  platform: Platform.OS,
};
let currentUserId: string | null = null;

/**
 * Checks whether native Firebase Crashlytics is available in the current environment.
 * Evaluates to true if running in a native bare or standalone build with native crashlytics linked.
 */
export function isCrashlyticsSupported(): boolean {
  return Platform.OS === "android" || Platform.OS === "ios";
}

/**
 * Checks whether automatic crash reporting and collection is currently enabled.
 */
export function isCrashlyticsCollectionEnabled(): boolean {
  return isCollectionEnabled;
}

/**
 * Toggles whether crash collection is enabled.
 */
export async function setCrashlyticsCollectionEnabled(enabled: boolean): Promise<void> {
  isCollectionEnabled = enabled;
  logAndFlush("info", `[Crashlytics] Crash collection enabled set to: ${enabled}`);
}

/**
 * Logs a breadcrumb or diagnostic message to Crashlytics.
 */
export function log(message: string): void {
  if (!isCollectionEnabled) return;
  console.info(`[Crashlytics Log] ${message}`);
  logAndFlush("info", `[Crashlytics] ${message}`);
}

/**
 * Sets the active user identifier for subsequent crash reports.
 */
export async function setUserId(userId: string): Promise<void> {
  currentUserId = userId;
  customAttributes.userId = userId;
  if (!isCollectionEnabled) return;
  logAndFlush("info", `[Crashlytics] User ID set to ${userId}`);
}

/**
 * Sets a custom diagnostic attribute key-value pair.
 */
export async function setAttribute(key: string, value: string): Promise<void> {
  customAttributes[key] = value;
}

/**
 * Records a non-fatal or caught exception to Crashlytics and flushes to the server pipeline.
 */
export async function recordError(error: any, context?: string): Promise<void> {
  if (!isCollectionEnabled) return;

  const errorMessage = error?.message || String(error) || "Unknown error";
  const errorStack = error?.stack || "No stack trace available";

  console.error(`[Crashlytics Error] [${context || "general"}] ${errorMessage}`, error);

  // 1. Record persistent crash tombstone to disk
  await recordCrashTombstone(error, false, context || "crashlytics_recordError");

  // 2. Queue and flush to remote logging
  logAndFlush(
    "error",
    `[CRASHLYTICS_EXCEPTION] context=${context || "none"} user=${currentUserId || "guest"} msg=${errorMessage}`,
    errorStack
  );
  await flushQueue();
}

/**
 * Retrieves comprehensive metadata about the active Crashlytics configuration.
 */
export function getCrashlyticsInfo(): ICrashlyticsInfo {
  const isNative = isCrashlyticsSupported();
  return {
    provider: isNative ? "native_crashlytics" : "cloud_logging_hybrid",
    platform: Platform.OS,
    collectionEnabled: isCollectionEnabled,
    projectId: FIREBASE_PROJECT_ID,
    googleServicesConfigured: true,
    appVersion: AppVersion,
    productVersion: ProductVersion,
    consoleUrl: `https://console.firebase.google.com/project/${FIREBASE_PROJECT_ID}/crashlytics`,
  };
}

/**
 * Triggers a simulated test crash for diagnostics and testing purposes.
 * Safely captures the test exception, saves a tombstone, and flushes to server logs.
 */
export async function testCrash(): Promise<{ success: boolean; message: string }> {
  const testError = new Error(`[Crashlytics Test Crash] Triggered on ${Platform.OS} at ${new Date().toISOString()}`);
  testError.name = "CrashlyticsTestException";

  await recordError(testError, "admin_test_crash");
  return {
    success: true,
    message: "Test crash successfully recorded and flushed to Crashlytics pipeline.",
  };
}

/**
 * Reads any persisted crash tombstones from local or session storage.
 */
export async function getRecentTombstones(): Promise<ICrashTombstone[]> {
  try {
    let rawContent: string | null = null;

    if (Platform.OS === "web" && typeof localStorage !== "undefined") {
      rawContent = localStorage.getItem(WEB_CRASH_STORAGE_KEY);
    }
    
    if (!rawContent) {
      const file = getCrashTombstoneFile();
      if (file && file.exists) {
        rawContent = await file.text();
      }
    }

    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      return Array.isArray(parsed) ? parsed : [parsed];
    }
  } catch (err) {
    console.warn("[CrashlyticsService] Failed to read recent tombstones:", err);
  }

  return [];
}

/**
 * Clears any locally stored crash tombstones.
 */
export async function clearRecentTombstones(): Promise<void> {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(WEB_CRASH_STORAGE_KEY);
    }
    const file = getCrashTombstoneFile();
    if (file && file.exists) {
      await file.delete();
    }
  } catch (err) {
    console.warn("[CrashlyticsService] Failed to clear tombstones:", err);
  }
}

/**
 * Unified Crashlytics service facade providing cross-platform crash reporting,
 * diagnostic logging, user attribution, and crash simulation tools.
 */
export const CrashlyticsService = {
  isSupported: isCrashlyticsSupported,
  isCollectionEnabled: isCrashlyticsCollectionEnabled,
  setCollectionEnabled: setCrashlyticsCollectionEnabled,
  log,
  setUserId,
  setAttribute,
  recordError,
  getInfo: getCrashlyticsInfo,
  testCrash,
  getRecentTombstones,
  clearRecentTombstones,
};

export default CrashlyticsService;
